import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiEndpoint } from '../../../../../config/api';
import { getToken, clearToken } from '../../../../../utils/auth';
import { clearWorkspaceStorage } from '../utils/storage';
import { sharedGet, peekCache, clearRequestCache } from '../utils/requestCache';

/** Auth headers for every workspace API call. */
export const authHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const isAuthEndpoint = (endpoint) => /\/api\/auth\/(login|register)/.test(String(endpoint || ''));

let redirectingToLogin = false;
/** Session expired: drop the token + workspace cache and go to /login (once). */
const handleUnauthorised = () => {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  try { clearToken(); } catch { /* ignore */ }
  clearWorkspaceStorage();
  clearRequestCache();
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    // Keep the user's page so Login can send them back (?next= must be a same-origin path).
    const here = `${window.location.pathname}${window.location.search}`;
    const safe = here.startsWith('/') && !here.startsWith('//') && !here.startsWith('/\\');
    window.location.href = safe && here !== '/' ? `/login?next=${encodeURIComponent(here)}` : '/login';
  }
};

/**
 * Thin fetch wrapper — resolves JSON (or null for 204 / empty bodies), throws on non-2xx.
 *
 * Thrown errors carry: `status` (HTTP code, 0 for network failure), `body`
 * (parsed JSON error body when present) and `network: true` when the request
 * never reached the server. A 401 clears the session and redirects to /login.
 * FormData bodies are sent without a JSON Content-Type.
 */
/** Cache key for a GET: the signed-in user's token plus the endpoint (a different user never shares). */
const cacheKey = (endpoint) => `${getToken() || ''}|${endpoint}`;
const isPlainGet = (options) => !options || ((!options.method || String(options.method).toUpperCase() === 'GET') && !options.body && !options.signal && !options.noCache);

/**
 * Plain GETs go through the shared request cache (utils/requestCache): identical reads
 * in flight together share one request, and a just-finished answer is reused for a
 * moment. Any other method clears the cache once it succeeds, so nothing stale shows
 * after a save. Pass `{ noCache: true }` to force a fresh read.
 */
export const apiFetch = (endpoint, options = {}) => {
  if (isPlainGet(options)) return sharedGet(cacheKey(endpoint), () => rawFetch(endpoint, options));
  const { noCache, ...rest } = options || {};
  return rawFetch(endpoint, rest).then((out) => {
    if (rest.method && String(rest.method).toUpperCase() !== 'GET') clearRequestCache();
    return out;
  });
};

const rawFetch = async (endpoint, options = {}) => {
  const headers = { ...authHeaders(), ...(options.headers || {}) };
  if (typeof FormData !== 'undefined' && options.body instanceof FormData) delete headers['Content-Type'];

  let res;
  try {
    res = await fetch(getApiEndpoint(endpoint), { ...options, headers });
  } catch (cause) {
    if (cause && cause.name === 'AbortError') throw cause;
    const err = new Error(`Network error ${endpoint}`);
    err.status = 0;
    err.network = true;
    err.cause = cause;
    throw err;
  }

  if (!res.ok) {
    const err = new Error(`API ${res.status} ${endpoint}`);
    err.status = res.status;
    try { err.body = await res.json(); } catch { /* non-JSON error body */ }
    if (res.status === 401 && !isAuthEndpoint(endpoint)) handleUnauthorised();
    throw err;
  }
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
};

/**
 * A human sentence for any error thrown by apiFetch (or anything else).
 *   403        → the server's message when the error is coded, else "You don't have permission to do that"
 *   network    → "Couldn't reach CloudSafe — check your connection"
 *   401        → "Your session has expired — sign in again"
 *   otherwise  → the server's own message, else a status-based fallback
 */
export function apiErrorMessage(err, fallback = 'Something went wrong — try again') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  const status = err.status;
  // A coded 403 (JOB_READ_ONLY, NO_COMPANY, ADMIN_ONLY…) carries a message meant for the user.
  if (status === 403) return (err.body && err.body.code && typeof err.body.message === 'string' && err.body.message) || "You don't have permission to do that";
  if (err.network || status === 0 || (err instanceof TypeError && /fetch|network/i.test(err.message || ''))) {
    return "Couldn't reach CloudSafe — check your connection";
  }
  if (status === 401) return 'Your session has expired — sign in again';
  // 503 = CloudSafe couldn't reach its database (writes are refused until it's back).
  // A coded 503 (e.g. MIGRATION_PENDING) carries its own message; otherwise say it plainly.
  if (status === 503 && !(err.body && err.body.code)) return 'CloudSafe is having trouble — try again shortly';
  const b = err.body;
  const serverMsg = b && (typeof b === 'string' ? b : (b.message || b.error || b.msg || (Array.isArray(b.errors) && b.errors[0] && (b.errors[0].msg || b.errors[0].message))));
  if (serverMsg && typeof serverMsg === 'string') return serverMsg;
  if (status === 404) return "That record couldn't be found — it may have been deleted";
  if (status === 409) return 'That conflicts with an existing record';
  if (status === 413) return 'That file is too large';
  if (status === 422 || status === 400) return 'Some details are missing or invalid';
  if (status >= 500) return 'CloudSafe had a problem — try again in a moment';
  return fallback;
}

/**
 * Fetch data from an endpoint.
 *
 * `initial` is the EMPTY shape held before the first response lands — `[]`,
 * `null`, `{}`. Never sample records. A successful response always wins,
 * empty included.
 *
 * @param {string|null} endpoint   pass null to skip fetching
 * @param {*} initial              empty shape
 * @param {(json:any)=>any} select map the raw payload
 * @param {object} [options]
 * @param {boolean} [options.keepPrevious=true]  while a NEW endpoint loads, keep showing the previous
 *        endpoint's data (good for paging a calendar). false → reset to `initial` immediately.
 * @param {boolean} [options.keepOnError=false]  on a failed reload keep the last good data (flagged
 *        `isStale: true`) instead of resetting to `initial`. Either way `error` is set — never silent.
 * @returns {{ data, loading, error, reload, refetch, hasLoaded, isStale, usingFallback }}
 *   `reload` and `refetch` are the same function (refetch kept for existing screens).
 *   `isStale` is true whenever `data` is NOT the answer for the current endpoint
 *   (previous endpoint still showing, or kept after an error).
 */
export function useApiData(endpoint, initial = null, select = (x) => x, options = {}) {
  const { keepPrevious = true, keepOnError = false } = options || {};
  // A recent answer for this endpoint shows at once; the fetch below still refreshes it.
  const seeded = useRef(undefined);
  if (seeded.current === undefined) {
    const raw = endpoint ? peekCache(cacheKey(endpoint)) : undefined;
    let mapped;
    if (raw !== undefined) { try { mapped = select(raw); } catch { mapped = undefined; } }
    seeded.current = mapped === undefined ? null : { value: mapped };
  }
  const [data, setData] = useState(seeded.current ? seeded.current.value : initial);
  const [loading, setLoading] = useState(!!endpoint);
  const [hasLoaded, setHasLoaded] = useState(!endpoint || !!seeded.current);
  const [error, setError] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const selectRef = useRef(select);
  selectRef.current = select;
  const initialRef = useRef(initial);
  const reqId = useRef(0);
  const alive = useRef(true);
  const lastEndpoint = useRef(endpoint);

  // Set true inside the effect too: StrictMode (dev) mounts, cleans up, then
  // mounts again, and a ref only initialised to true would stay false.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    const endpointChanged = lastEndpoint.current !== endpoint;
    lastEndpoint.current = endpoint;
    if (!endpoint) {
      setData(initialRef.current); setLoading(false); setError(null); setIsStale(false); setHasLoaded(true);
      return;
    }
    if (endpointChanged) {
      if (keepPrevious) setIsStale(true);
      else { setData(initialRef.current); setIsStale(false); }
    }
    setLoading(true);
    try {
      const json = await apiFetch(endpoint);
      if (!alive.current || id !== reqId.current) return;
      const mapped = selectRef.current(json);
      setData(mapped === undefined ? initialRef.current : mapped);
      setError(null);
      setIsStale(false);
    } catch (e) {
      if (!alive.current || id !== reqId.current) return;
      setError(e);
      if (keepOnError) setIsStale(true);
      else { setData(initialRef.current); setIsStale(false); }
    } finally {
      if (alive.current && id === reqId.current) {
        setLoading(false);
        setHasLoaded(true);
      }
    }
  }, [endpoint, keepPrevious, keepOnError]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, reload: load, refetch: load, hasLoaded, isStale, usingFallback: !!error || !endpoint };
}

export default useApiData;
